<script setup>
import { useToast } from "vue-toastification";

import api from "@/api";
import HtmlToast from "@/components/HtmlToast.vue";

import { useAttachments } from "@/composables/useAttachments";
import { useDepartmentStore } from "@/stores/department";
import { useTeamStore } from "@/stores/team";
import { useUserStore } from "@/stores/user";
import FullPageLoader from "@/components/ui-kit/base/FullPageLoader.vue";
import AttachmentCoursel from "@/views/messenger/components/AttachmentCoursel.vue";

// Copied from InteractionConversationsComposeForm.vue.
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MiB

const toast = useToast();
const deptStore = useDepartmentStore();
const userStore = useUserStore();
const teamStore = useTeamStore();
const { attachments, handleFileChange, handleFileDelete } = useAttachments({
  max_size: MAX_FILE_SIZE,
});

const emit = defineEmits([
  "close",
]);

defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

const creatingTicket = ref(false);
const showConfirm = ref(false);
const isLoading = ref(false);
const fileInput = ref(null);

const setIsLoading = (state) => {
  isLoading.value = state;
};

const formData = ref({
  beneficiary_user_id: null,
  text: '',
  title: '',
  department_id: null,
  assignee_type: null,
  team: null,
  agents: [],
  notify_user: false,
});

const triggerFileSelect = () => {
  fileInput.value.click();
};

const closeModal = () => {
  if (creatingTicket.value) return;

  formData.value = {};
  creatingTicket.value = false;

  emit("close", {});
};

const closeEmailConfirmation = () => {
  emit("close", {});
};

const isFormValid = () => {
  const isValid = (
    !!formData.value.beneficiary_user_id &&
    !!formData.value.department_id &&
    !!formData.value.title
  );
  return isValid;
};

const beneficiaryName = computed(() => {
  const id = formData.value.beneficiary_user_id;

  if (!id) return '';

  const opt = (userStore.allUsersDropdownOptions ?? []).find(o => o.value === id)
  return opt?.text || '';
});

// Logic from InteractionConversationsComposeForm.vue.
const uploadAttachments = async () => {
  return await Promise.all(
    attachments.value.map(async (att) => {
      const { url, fields } = await api.ticket_responses.getPresignedUrl(
        att.name, att.type ?? "application/octet-stream"
      );

      // Upload file to S3
      const fd = new FormData();
      Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
      fd.append("file", att);
      const uploadResp = await fetch(url, { method: "POST", body: fd });
      if (!uploadResp.ok) {
        console.error(`Failed to upload ${att.name} to ${url} due to ${uploadResp.status}`);
        toast.error(`Failed to upload attachment: ${att.name}`);
        throw new Error(`Failed to upload attachment: ${att.name}`);
      }

      // Follows models.S3Attachment.
      const urlObj = new URL(url);
      return {
        file_name: att.name,
        s3_key: fields.key,
        file_url: `${url}${fields.key}`,
        bucket: urlObj.hostname.split(".")[0],
      };
    })
  );
}

const createTicket = async (notify) => {
  if (typeof notify !== "boolean") {
    if (!isFormValid() || creatingTicket.value) return;
    showConfirm.value = true;
    return;
  }

  if (!isFormValid()) return;

  creatingTicket.value = true;
  setIsLoading(true);
  if (formData.value.assignee_type === "team")
    formData.value.agents = [];
  else
    formData.value.team = null;

  formData.value.notify_user = notify;

  let response;
  try {
    const processedAttachments = await uploadAttachments();
    response = await api.interactions.createTicket({
      ...formData.value,
      organization_id: userStore.activeOrganization,
      attachments: processedAttachments,
    });
  }
  catch (error) {
    creatingTicket.value = false;
    showConfirm.value = false;
    // Don't clear attachments on error.
    // attachments.value = [];
    return;
  } finally {
     setIsLoading(false);
  }

  toast.success(
    {
      component: HtmlToast,
      props: {
        html: `Ticket created<a href="/inbox/${response._id}" style="color: #fff; font-weight: bold;"></a>`,
      },
    }, 
    {
      timeout: 20000,
    }
  );

  creatingTicket.value = false;
  showConfirm.value = false;
  attachments.value = [];
  closeModal();
};

</script>

<template>
  <FullPageLoader :show="isLoading"></FullPageLoader>
  <BModal
    class="create-ticket-modal"
    title="Create Ticket"
    hide-footer
    no-close-on-backdrop
    no-close-on-esc
    :model-value="modelValue"
    @close="closeModal"
  >
    <div>
      <div class="beneficiary">
        <SelectInput
          :options="userStore.loading ? [] : userStore.allSupportableUsersWithEmailDropdownOptions"
          :tryToFindNameInOptionsOnFirstLoad="true"
          :reduce="(option) => option.value"
          v-model="formData.beneficiary_user_id"
          label="Person receiving support"
        />
      </div>

      <div class="mt-2">
        <label
          class="select-inp-label w-100 p-0 text-left text-decoration-none text-neutral-350 fw-bold"
        >
          Title
        </label>
        <TextareaInput
          v-model="formData.title"
          :rows="1"
        />
      </div>

      <div class="mt-2">
        <label
          class="select-inp-label w-100 p-0 text-left text-decoration-none text-neutral-350 fw-bold"
        >
          Description
        </label>
        <TextareaInput
          v-model="formData.text"
          :rows="8"
        />
      </div>

      <div class="department">
        <SelectInput
          class="mt-2"
          :options="deptStore.loading ? [] : deptStore.dropdownOptions"
          :tryToFindNameInOptionsOnFirstLoad="true"
          :reduce="(option) => option.value"
          v-model="formData.department_id"
          label="Department"
        />
      </div>

      <div class="assignees">
        <SelectInput
          class="mt-2"
          :options="[
            {value: 'team', text: 'Team'},
            {value: 'agents', text: 'Agents'},
          ]"
          :tryToFindNameInOptionsOnFirstLoad="true"
          :reduce="(option) => option.value"
          v-model="formData.assignee_type"
          label="Assignee"
        />

        <SelectInput
          v-if="formData.assignee_type == 'agents'"
          class="mt-2"
          :options="userStore.loading ? [] : userStore.hqActiveUsersDropdownOptions"
          :tryToFindNameInOptionsOnFirstLoad="true"
          :reduce="(option) => option.value"
          v-model="formData.agents"
          multiple=true
          placeholder="Agents"
        />
        <SelectInput
          v-else-if="formData.assignee_type == 'team'"
          class="mt-2"
          :options="teamStore.loading ? [] : teamStore.dropdownOptions"
          :tryToFindNameInOptionsOnFirstLoad="true"
          :reduce="(option) => option.value"
          v-model="formData.team"
          placeholder="Team"
        />
      </div>

      <div class="attachments mt-2">
        <label
          class="select-inp-label w-100 p-0 text-left text-decoration-none text-neutral-350 fw-bold"
        >
          Attachments
        </label>
        <custom-button button-type="blue" @click="triggerFileSelect">
          <Icon icon="paperclip" />Attach
          <input
            ref="fileInput"
            type="file"
            multiple
            @change="handleFileChange"
            style="display: none"
          />
        </custom-button>
        <AttachmentCoursel
          v-if="attachments.length"
          :attachments="attachments"
          @delete-file="handleFileDelete"
        />
      </div>

      <div class="my-mt-4 d-flex justify-content-end">
        <custom-button
          :disabled="!isFormValid() || creatingTicket"
          has-font-semi-bold
          button-type="blue"
          @click="createTicket(false)"
        >
          Create Ticket
        </custom-button>
      </div>
    </div>
  </BModal>

  <BModal
    v-model="showConfirm"
    title="Send email notification?"
    no-close-on-backdrop
    hide-header-close
    hide-footer
    centered
  >
    <p class="mb-3">
      Do you want to email <strong>{{ beneficiaryName || 'this person' }}</strong> that the ticket was created?
    </p>
    <div class="d-flex justify-content-end gap-2">
      <custom-button
        :disabled="creatingTicket"
        button-type="blue"
        has-font-semi-bold
        @click="createTicket(true)"
      >
        Yes
      </custom-button>
      <custom-button
        :disabled="creatingTicket"
        button-type="gray"
        @click="createTicket(false)"
      >
        No
      </custom-button>

    </div>
  </BModal>
</template>

<style lang="scss">
.create-ticket-modal {
  .modal-dialog {
    --bs-modal-width: 600px;
  }
}
</style>
